import { useMemo, useState } from "react";
import { movies, novels, comics, audiobooks } from "@/mocks/homeData";

interface LibraryItem {
  id: string;
  title: string;
  type: string;
  typeKey: string;
  meta: string;
  desc: string;
  cover?: string;
  icon: string;
}

const libraryItems: LibraryItem[] = [
  ...movies.map((m) => ({
    id: m.id,
    title: m.title,
    type: "影视",
    typeKey: "movie",
    meta: `${m.genre} · ${m.episode}`,
    desc: m.desc,
    cover: m.cover,
    icon: "ri-film-line",
  })),
  ...novels.map((n) => ({
    id: n.id,
    title: n.title,
    type: "小说",
    typeKey: "novel",
    meta: `${n.genre} · ${n.status} · ${n.author}`,
    desc: n.intro,
    icon: "ri-book-2-line",
  })),
  ...comics.map((c) => ({
    id: c.id,
    title: c.title,
    type: "漫画",
    typeKey: "comic",
    meta: `${c.genre} · ${c.update}`,
    desc: `竖屏条漫，适配手机阅读，更新进度：${c.update}。`,
    cover: c.cover,
    icon: "ri-image-2-line",
  })),
  ...audiobooks.map((a) => ({
    id: a.id,
    title: a.title,
    type: "听书",
    typeKey: "audio",
    meta: `${a.tag} · ${a.episodes} · ${a.narrator}`,
    desc: a.intro,
    icon: "ri-headphone-line",
  })),
];

const tabs = [
  { key: "all", label: "全部" },
  { key: "movie", label: "影视" },
  { key: "novel", label: "小说" },
  { key: "comic", label: "漫画" },
  { key: "audio", label: "听书" },
];

interface LibraryProps {
  query: string;
  onQueryChange: (value: string) => void;
}

export default function LibrarySection({ query, onQueryChange }: LibraryProps) {
  const [tab, setTab] = useState("all");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return libraryItems.filter((item) => {
      const matchTab = tab === "all" || item.typeKey === tab;
      if (!matchTab) return false;
      if (!q) return true;
      return (
        item.title.toLowerCase().includes(q) ||
        item.meta.toLowerCase().includes(q) ||
        item.desc.toLowerCase().includes(q) ||
        item.type.includes(q)
      );
    });
  }, [query, tab]);

  return (
    <section id="library" className="w-full bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-2xl">
          <span className="text-xs md:text-sm font-label tracking-[0.2em] text-primary-600 uppercase">
            Online Search
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            在线检索本页全部内容
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
            在下方输入关键词，即可在当前已收录的影视、小说、漫画与听书条目中快速定位。
            检索与筛选仅作用于本页内容，不接入外部接口，结果即时呈现。
          </p>
        </div>

        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex items-center flex-1 gap-2 px-3 rounded-lg bg-background-50 border border-background-200">
              <i className="ri-search-line text-foreground-500 text-base" aria-hidden="true"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="输入书名、片名或题材关键词"
                aria-label="检索本页内容"
                className="w-full bg-transparent py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              {query && (
                <button
                  type="button"
                  aria-label="清除关键词"
                  onClick={() => onQueryChange("")}
                  className="w-6 h-6 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-800 cursor-pointer"
                >
                  <i className="ri-close-circle-line" aria-hidden="true"></i>
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    tab === t.key
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 border border-background-200 hover:border-secondary-300 hover:text-secondary-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
            <span>
              共找到 <span className="font-bold text-primary-700">{results.length}</span> 条相关结果
            </span>
            <span className="hidden sm:block">输入关键词后可进一步缩小范围</span>
          </div>
        </div>

        {results.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {results.map((item) => (
              <article
                key={`${item.typeKey}-${item.id}`}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-300"
              >
                <div className="w-16 h-20 md:w-20 md:h-24 rounded-md overflow-hidden bg-background-200 shrink-0 flex items-center justify-center">
                  {item.cover ? (
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面`}
                      title={`${item.title} ${item.type}`}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <i className={`${item.icon} text-2xl text-secondary-600`} aria-hidden="true"></i>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-800 text-[11px] whitespace-nowrap">
                      {item.type}
                    </span>
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 truncate">
                      {item.title}
                    </h3>
                  </div>
                  <p className="mt-1 text-[11px] text-foreground-500 truncate">{item.meta}</p>
                  <p className="mt-2 text-xs text-foreground-600 leading-relaxed line-clamp-2">
                    {item.desc}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center py-14 rounded-lg border border-dashed border-background-300 bg-background-100 text-center">
            <div className="w-14 h-14 rounded-full bg-background-200 flex items-center justify-center text-2xl text-foreground-500">
              <i className="ri-search-eye-line" aria-hidden="true"></i>
            </div>
            <p className="mt-4 text-sm text-foreground-700">没有找到与「{query}」相关的内容</p>
            <p className="mt-1 text-xs text-foreground-500">换个关键词，或切换到「全部」分类再试试</p>
            <button
              type="button"
              onClick={() => {
                onQueryChange("");
                setTab("all");
              }}
              className="mt-5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              重置检索条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}